// =============================================================================
// scsImport.ts -- Patch 016.1.B.
//
// API client for the SCS import lane (POST upload + POST parse + POST
// confirm). Heritage uploads an externally-prepared SCS PDF, the Worker
// AI-parses it via Anthropic, and the operator confirms (with inline edits)
// before scs_lines materialize. xlsx upload is reserved for a follow-up
// sub-patch — Worker returns 415 scs_xlsx_not_yet_supported for now.
// =============================================================================

import { BASE, request, ApiError } from './request'

export interface ScsUploadResult {
  ok: true
  document_id: string
  original_filename: string
  size_bytes: number
  mime_type: string
  request_id: string
}

export interface ScsParseLine {
  line_number: number
  display_name: string
  category: string | null
  original_budget_cents: number | null
  notes: string | null
}

export interface ScsParseMetadata {
  prompt_version: string
  model: string
  token_usage_input: number
  token_usage_output: number
  cost_estimate_usd: number
  latency_ms: number
}

export interface ScsParseResult {
  ok: true
  document_id: string
  proposed_lines: ScsParseLine[]
  metadata: ScsParseMetadata
  request_id: string
}

export interface ScsConfirmLineInput {
  line_number: number
  display_name: string
  category?: string | null
  original_budget_cents?: number | null
  notes?: string | null
}

export interface ScsConfirmResult {
  ok: true
  archived_count: number
  inserted_count: number
  scs_line_ids: string[]
  request_id: string
}

export const scsImportApi = {
  upload: async (projectId: string, file: File): Promise<ScsUploadResult> => {
    const form = new FormData()
    form.append('file', file)
    const res = await fetch(
      `${BASE}/api/projects/${projectId}/scs-import/upload`,
      {
        method: 'POST',
        credentials: 'include',
        body: form,
      },
    )
    const body = (await res.json().catch(() => null)) as unknown
    if (!res.ok) {
      const err = (body as { error?: string })?.error ?? `http_${res.status}`
      throw new ApiError(err, res.status, body)
    }
    return body as ScsUploadResult
  },

  parse: (projectId: string, documentId: string) =>
    request<ScsParseResult>(
      `/api/projects/${projectId}/scs-import/parse`,
      {
        method: 'POST',
        body: JSON.stringify({ document_id: documentId }),
      },
    ),

  confirm: (
    projectId: string,
    sourceDocumentId: string,
    lines: ScsConfirmLineInput[],
  ) =>
    request<ScsConfirmResult>(
      `/api/projects/${projectId}/scs-import/confirm`,
      {
        method: 'POST',
        body: JSON.stringify({
          source_document_id: sourceDocumentId,
          lines,
        }),
      },
    ),
}
